// UPPDRAG-39 — pure scoring computation for league matches.
//
// Extracted from ReportResult.tsx so the rules are vitest-able. The logic
// for time-limited (incomplete) sets follows the configurable
// leader-rule:
//   - tied (diff===0)                   → scoring_system.unfinished_set_tied (default 1/1)
//   - diff >= league.incomplete_set_lead_threshold → scoring_system.set_win / set_loss
//   - 0 < diff < threshold              → league.incomplete_set_share_winner /
//                                          league.incomplete_set_share_loser
//
// For non-time-limited sets (or when the set was actually finished):
//   - winner gets scoring_system.set_win, loser gets scoring_system.set_loss

export interface SetInputScore {
  /** Empty string is treated as "no input" — set is skipped. */
  home: number | string;
  away: number | string;
}

export interface ScoringSystemValues {
  set_win?: number;
  set_loss?: number;
  unfinished_set_tied?: number;
}

export interface IncompleteSetRule {
  /** Game-lead required for an incomplete set to count as a set win. */
  leadThreshold: number;
  /** Points for the slight leader when 0 < diff < leadThreshold. */
  shareWinner: number;
  /** Points for the slight trailer when 0 < diff < leadThreshold. */
  shareLoser: number;
}

export interface CalcInputs {
  sets: SetInputScore[];
  scoringSystem: ScoringSystemValues;
  incompleteSetRule: IncompleteSetRule;
  isTimeLimited: boolean;
}

export interface CalcResult {
  home: number;
  away: number;
}

/**
 * Calculate match points from the per-set scores. Pure: no I/O.
 */
export function calculateMatchPoints(input: CalcInputs): CalcResult {
  const { sets, scoringSystem, incompleteSetRule, isTimeLimited } = input;
  const setWin = scoringSystem.set_win ?? 3;
  const setLoss = scoringSystem.set_loss ?? 0;
  const tiedShare = scoringSystem.unfinished_set_tied ?? 1;
  const { leadThreshold, shareWinner, shareLoser } = incompleteSetRule;

  let home = 0;
  let away = 0;

  for (const set of sets) {
    if (set.home === "" || set.away === "") continue;
    const h = Number(set.home);
    const a = Number(set.away);
    if (Number.isNaN(h) || Number.isNaN(a)) continue;

    const diff = Math.abs(h - a);

    if (isTimeLimited && h === a) {
      // Tied incomplete set
      home += tiedShare;
      away += tiedShare;
    } else if (isTimeLimited && diff < leadThreshold) {
      // Slight lead, below decisive threshold → share rule
      if (h > a) { home += shareWinner; away += shareLoser; }
      else       { away += shareWinner; home += shareLoser; }
    } else {
      // Either: not time-limited (set finished normally), OR
      //         diff >= threshold (counts as a set win even if time-out)
      if (h > a) { home += setWin; away += setLoss; }
      else       { away += setWin; home += setLoss; }
    }
  }

  return { home, away };
}
